import { useEffect } from 'react';
import { GridRowModel } from '@mui/x-data-grid-pro';
import {
  setAlert,
  useAppDispatch,
  useAppSelector,
  BaseRequestParams,
  initializeTableData,
  TableData,
  resetTableData,
  generateColumns,
} from 'api';
import { AlertSeverity } from 'api/models/alert';
import {
  PayerType,
  useCreatePayerTypeMutation,
  useGetPayerTypeQuery,
} from 'api/models/databricks/payerType';
import { ActionType, DataGridTable } from 'app/common';

export const PayerTypeTable = () => {
  // Hooks
  const { currentMenuItem } = useAppSelector(state => state.menuItem);
  const { rows } = useAppSelector(state => state.dataGridTable);
  const { mockUser } = useAppSelector(state => state.appUser);
  const mid = currentMenuItem?.id.toString() ?? '';
  const uid = mockUser?.id.toString() ?? '';
  const { data, isLoading, isFetching } = useGetPayerTypeQuery(
    { mid: mid, uid },
    { skip: mid === '-1' || uid === '-1' }
  );

  const [createPayerType, { isLoading: isCreating }] =
    useCreatePayerTypeMutation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(resetTableData());
    const tableData: TableData = {
      rows: data?.rows,
      columns: generateColumns(data?.columns),
    };

    dispatch(initializeTableData(tableData));
  }, [dispatch, data]);

  const processRowUpdate = async (rowUpdate: GridRowModel) => {
    let updatedRow = { ...rowUpdate, isNew: false };

    if (rowExists(rowUpdate as PayerType)) {
      dispatch(
        setAlert({
          message: 'Row already exists.',
          severity: AlertSeverity.Error,
        })
      );
      return;
    }

    if (rowUpdate.isNew) {
      const reqBody = {
        request: { mid, uid } as BaseRequestParams,
        payerType: {
          ...(rowUpdate as PayerType),
          payerType: rowUpdate.payerType.trim(),
        },
      };
      try {
        await createPayerType(reqBody)
          .unwrap()
          .then(() =>
            dispatch(
              setAlert({
                message: 'Row added.',
                severity: AlertSeverity.Success,
              })
            )
          );
      } catch (err: any) {
        dispatch(
          setAlert({
            message: err.message,
            severity: AlertSeverity.Error,
          })
        );
        return;
      }
    }

    return updatedRow;
  };

  function rowExists(newRow: PayerType) {
    let found = false;
    rows.forEach(row => {
      if (
        row.payerType &&
        row.payerType.toUpperCase() === newRow.payerType.toUpperCase()
      ) {
        found = true;
        return;
      }
    });
    return found;
  }

  return (
    <DataGridTable
      cellActions={[ActionType.SaveOnly]}
      processRowUpdate={processRowUpdate}
      isBusy={isFetching || isLoading || isCreating}
      toolbarProps={{ enableAddButton: true, enableFilter: true }}
    />
  );
};
